import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle, Info, Loader } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

// Keep SyncStatus interface consistent with store
interface SyncStatus {
  status: 'pending' | 'running' | 'completed' | 'error' | 'idle';
  message: string;
  progress: number;
  details?: string[];
}

interface SyncStatusIndicatorProps {
  taskId?: string | null; // Make taskId optional
  onSyncComplete?: () => void; // Optional callback when sync finishes (completed or error)
}

const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ taskId: propTaskId, onSyncComplete }) => {
  const [displayStatus, setDisplayStatus] = useState<SyncStatus>({
    status: 'idle',
    message: 'Awaiting sync task...',
    progress: 0,
  });
  const [isShowingSuccess, setIsShowingSuccess] = useState<boolean>(false);
  const [completedTaskInfo, setCompletedTaskInfo] = useState<SyncStatus | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const hasFinishedCallbackCalledRef = useRef<boolean>(false);
  const activeTaskIdRef = useRef<string | null>(null);
  const successTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Get required state/setters from store - Keep reading global state for initialization
  const { accessToken, globalSyncTaskId, globalSyncTaskStatus, setSyncTaskStatus } = useAuthStore(state => ({
      accessToken: state.accessToken,
      globalSyncTaskId: state.syncTaskId,
      globalSyncTaskStatus: state.syncTaskStatus,
      setSyncTaskStatus: state.setSyncTaskStatus, // Keep for updating global state if needed
  }));

  // Determine the initial task ID to potentially track
  const initialTaskId = propTaskId ?? globalSyncTaskId;

  // --- Modified useEffect ---
  useEffect(() => {
    // --- Task Initialization Logic ---
    // Determine the task ID to track for this effect run
    const currentTargetTaskId = propTaskId ?? globalSyncTaskId; // Use latest prop/global ID

    // Reset finished flag if the target task ID changes
    if (activeTaskIdRef.current !== currentTargetTaskId) {
        hasFinishedCallbackCalledRef.current = false;
        // If there was an old task, clear its status locally
        if (activeTaskIdRef.current) {
             setDisplayStatus({ status: 'idle', message: 'Task changed.', progress: 0 });
        }
    }

    // Update the actively tracked task ID
    activeTaskIdRef.current = currentTargetTaskId;

    // Initialize local state based on the target task ID
    if (currentTargetTaskId) {
        // Prioritize existing global state if it matches the target task
        if (globalSyncTaskId === currentTargetTaskId && globalSyncTaskStatus && globalSyncTaskStatus.status !== 'idle') {
            console.log(`[SyncIndicator Init/Update] Using existing global status for ${currentTargetTaskId}:`, globalSyncTaskStatus.status);
            // Only update local state if it's different or idle, avoid overwriting running state unnecessarily
            setDisplayStatus(prev => {
                if (prev.status === 'idle' || prev.status === 'pending' || JSON.stringify(prev) !== JSON.stringify(globalSyncTaskStatus)) {
                    return globalSyncTaskStatus;
                }
                return prev;
            });
            if (globalSyncTaskStatus.status === 'completed' || globalSyncTaskStatus.status === 'error') {
                hasFinishedCallbackCalledRef.current = true;
            }
        } else {
            // No relevant global state, or global state is idle. Set local to pending if not already finished.
            setDisplayStatus(prev => {
                if (prev.status !== 'completed' && prev.status !== 'error') {
                    console.log(`[SyncIndicator Init/Update] No relevant global status for ${currentTargetTaskId}. Setting local to pending.`);
                    // Also update global state if this component initiates the pending status
                    const pendingStatus: SyncStatus = { status: 'pending', message: 'Sync initiated, connecting...', progress: 0, details: prev.details }; // Keep details if any
                    setSyncTaskStatus(pendingStatus); // Update global state
                    return pendingStatus;
                }
                return prev; // Keep existing completed/error state
            });
        }
    } else {
        // No target task ID - but don't immediately reset if we're showing a success state
        if (!isShowingSuccess && displayStatus.status !== 'completed') {
            console.log(`[SyncIndicator Init/Update] No target Task ID. Setting local to idle.`);
            setDisplayStatus({ status: 'idle', message: 'No active sync task.', progress: 0 });
        } else if (isShowingSuccess) {
            console.log(`[SyncIndicator Init/Update] No target Task ID but preserving success state display`);
        }
    }
    // --- End Task Initialization Logic ---    // --- SSE Connection Logic ---
    const trackedId = activeTaskIdRef.current; // Use the ref which holds the ID we are tracking
    let shouldConnect = false;

    // CRITICAL: Only connect for pending/running states. Never connect for completed/error/idle states.
    // Also never connect if the task has already finished (prevents reconnection after completion)
    if (trackedId && accessToken && 
        (displayStatus.status === 'pending' || displayStatus.status === 'running') &&
        !hasFinishedCallbackCalledRef.current) {
        shouldConnect = true;
    }

    // Close existing connection if we should not connect OR if tracked ID is now null
    if ((!shouldConnect || !trackedId) && eventSourceRef.current) {
        console.log(`[SyncIndicator SSE] Closing connection for ${trackedId} (shouldConnect: ${shouldConnect}, trackedId: ${trackedId}, status: ${displayStatus.status}, finished: ${hasFinishedCallbackCalledRef.current}).`);
        eventSourceRef.current.close();
        eventSourceRef.current = null;
    }

    // Establish connection if needed and not already connected
    if (shouldConnect && !eventSourceRef.current) {
        // trackedId and accessToken are non-null here because shouldConnect is true
        console.log(`[SyncIndicator SSE] Setting up EventSource for tracked task ID: ${trackedId!}`);
        const apiUrl = import.meta.env.VITE_API_BASE_URL || '/api';
        const sseUrl = `${apiUrl}/trades/sync/status/${trackedId!}?token=${encodeURIComponent(accessToken!)}`;
        console.log(`[SyncIndicator SSE] URL: ${sseUrl}`);

        try {
            const es = new EventSource(sseUrl);
            eventSourceRef.current = es;

            es.onopen = () => {
                console.log(`[SyncIndicator SSE] Connection opened for task ${trackedId!}`);
                // Update local status only if still pending
                setDisplayStatus(prev => {
                    if (prev.status === 'pending') {
                        const runningStatus: SyncStatus = { ...prev, status: 'running', message: 'Connected, waiting for updates...', progress: prev.progress > 0 ? prev.progress : 5 };
                        // Optionally update global state here too if desired, but GlobalListener might handle it
                        // setSyncTaskStatus(runningStatus);
                        return runningStatus;
                    }
                    return prev;
                });
            };

            es.onmessage = (event) => {
                // Check if we are still tracking *this* task ID before processing
                if (activeTaskIdRef.current !== trackedId) {
                    console.warn(`[SyncIndicator SSE] Received message for ${trackedId} but now tracking ${activeTaskIdRef.current}. Ignoring.`);
                    if(eventSourceRef.current) eventSourceRef.current.close(); // Close the old connection
                    eventSourceRef.current = null;
                    return;
                }
                try {
                    const data: SyncStatus = JSON.parse(event.data);
                    console.log('[SyncIndicator SSE] Message received:', data.status, data.message, data.progress, data.details);

                    // --- Update Local State ---
                    setDisplayStatus(data); // Update local state directly
                    // --- Optionally update global state ---
                    // setSyncTaskStatus(data); // Let GlobalListener handle global state updates primarily

                    // Handle completion locally with success state persistence
                    if ((data.status === 'completed' || data.status === 'error') && !hasFinishedCallbackCalledRef.current) {
                        if (eventSourceRef.current) {
                            eventSourceRef.current.close();
                            eventSourceRef.current = null;
                        }
                        console.log(`[SyncIndicator SSE] Connection closed by message for task ${trackedId} due to status: ${data.status}`);
                        
                        // Store completion info and start success display timer
                        if (data.status === 'completed') {
                            setCompletedTaskInfo(data);
                            setIsShowingSuccess(true);
                            
                            // Clear any existing timer
                            if (successTimerRef.current) {
                                clearTimeout(successTimerRef.current);
                            }
                            
                            // Keep success state visible for 4 seconds
                            successTimerRef.current = setTimeout(() => {
                                console.log(`[SyncIndicator] Success display timer expired - transitioning to idle`);
                                setIsShowingSuccess(false);
                                setCompletedTaskInfo(null);
                                setDisplayStatus({ status: 'idle', message: 'Sync completed successfully.', progress: 0 });
                            }, 4000);
                        }
                        
                        if (onSyncComplete) {
                            onSyncComplete();
                        }
                        hasFinishedCallbackCalledRef.current = true;
                    }
                } catch (error) {
                    console.error('[SyncIndicator SSE] Failed to parse message:', event.data, error);
                    const errorStatus: SyncStatus = { status: 'error', message: 'Error parsing status update.', progress: 100 };
                    setDisplayStatus(errorStatus); // Update local state
                    // setSyncTaskStatus(errorStatus); // Optionally update global
                    if (eventSourceRef.current) {
                        eventSourceRef.current.close();
                        eventSourceRef.current = null;
                    }
                    if (onSyncComplete && !hasFinishedCallbackCalledRef.current) {
                        onSyncComplete();
                        hasFinishedCallbackCalledRef.current = true;
                    }
                }
            };            es.onerror = (error) => {
                if (activeTaskIdRef.current !== trackedId) {
                     console.warn(`[SyncIndicator SSE] Received error for ${trackedId} but now tracking ${activeTaskIdRef.current}. Ignoring.`);
                     return;
                }
                
                // CRITICAL FIX: Don't change status to 'error' if task already completed successfully
                // This prevents infinite reconnection loops when SSE closes after completion
                if (hasFinishedCallbackCalledRef.current) {
                    console.log(`[SyncIndicator SSE] Error received for ${trackedId} but task already finished. Ignoring to prevent reconnection loop.`);
                    return;
                }
                
                console.error(`[SyncIndicator SSE] Error for task ${trackedId}:`, error);
                const errorStatus: SyncStatus = { status: 'error', message: 'Connection error or task expired.', progress: 100 };
                setDisplayStatus(errorStatus); // Update local state
                // setSyncTaskStatus(errorStatus); // Optionally update global
                // Don't close connection on error, let EventSource handle retries/closure
                if (onSyncComplete) {
                    onSyncComplete();
                    hasFinishedCallbackCalledRef.current = true;
                }
            };        } catch (error) {
            if (activeTaskIdRef.current === trackedId && !hasFinishedCallbackCalledRef.current) { // Check if we were trying to track this task and it hasn't finished
                console.error(`[SyncIndicator SSE] Failed to create EventSource for task ${trackedId}:`, error);
                const errorStatus: SyncStatus = { status: 'error', message: 'Failed to initiate status connection.', progress: 100 };
                setDisplayStatus(errorStatus); // Update local state
                // setSyncTaskStatus(errorStatus); // Optionally update global
                if (onSyncComplete) {
                    onSyncComplete();
                    hasFinishedCallbackCalledRef.current = true;
                }
            }
        }
    } else if (!accessToken && trackedId && (displayStatus.status === 'pending' || displayStatus.status === 'running') && !hasFinishedCallbackCalledRef.current) {        // Handle missing token
        console.warn(`[SyncIndicator SSE] Connection skipped for task ${trackedId}: No access token.`);
        const errorStatus: SyncStatus = { status: 'error', message: 'Authentication token missing.', progress: 0 };
        setDisplayStatus(errorStatus); // Update local state
        // setSyncTaskStatus(errorStatus); // Optionally update global
        if (onSyncComplete) {
            onSyncComplete();
            hasFinishedCallbackCalledRef.current = true;
        }
    }

    // --- Cleanup Function ---
    return () => {
        // Only close the connection if it belongs to the task ID this effect instance was managing
        if (eventSourceRef.current && activeTaskIdRef.current === trackedId) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
            console.log(`[SyncIndicator Cleanup] EventSource closed for task ID: ${trackedId}`);
        }
        
        // Clean up success timer
        if (successTimerRef.current) {
            clearTimeout(successTimerRef.current);
            successTimerRef.current = null;
        }
    };
    // --- Simplified Dependency Array ---
    // React primarily to the derived initialTaskId and the accessToken.
    // Reads of global state inside are fine, but don't trigger re-runs unless initialTaskId changes.
  }, [initialTaskId, accessToken, onSyncComplete, setSyncTaskStatus, propTaskId, globalSyncTaskId, globalSyncTaskStatus]); // Keep global reads for init logic, add propTaskId

  // --- Rendering Logic with success celebration ---
  const getStatusIcon = () => {
    const status = effectiveStatus.status;
    switch (status) {
      case 'running':
      case 'pending':
        return <Loader className="h-5 w-5 animate-spin text-blue-400" />;
      case 'completed':
        return (
          <motion.div
            animate={isShowingSuccess ? {
              scale: [1, 1.2, 1],
              rotate: [0, 5, -5, 0]
            } : {}}
            transition={{ duration: 0.6, repeat: isShowingSuccess ? 2 : 0 }}
          >
            <CheckCircle className={`h-5 w-5 ${
              isShowingSuccess ? 'text-green-300' : 'text-green-400'
            }`} />
          </motion.div>
        );
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-400" />;
      case 'idle':
      default:
        return <Info className="h-5 w-5 text-white/60" />;
    }
  };

  const getProgressBarColor = () => {
    const status = effectiveStatus.status;
    switch (status) {
      case 'running':
      case 'pending':
        return 'bg-blue-400';
      case 'completed':
        return isShowingSuccess 
          ? 'bg-gradient-to-r from-green-400 to-green-300 shadow-lg shadow-green-400/25' 
          : 'bg-green-400';
      case 'error':
        return 'bg-red-400';
      case 'idle':
      default:
        return 'bg-white/30';
    }
  };

  // Visibility: show if not idle OR if showing success celebration
  const isVisible = displayStatus.status !== 'idle' || isShowingSuccess;
  
  // Use completion info during success display, otherwise use current status
  const effectiveStatus = isShowingSuccess && completedTaskInfo ? completedTaskInfo : displayStatus;
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className={`p-4 mt-4 backdrop-blur-sm border rounded-xl shadow-lg transition-all duration-500 ${
            isShowingSuccess 
              ? 'bg-green-500/20 border-green-400/30 ring-2 ring-green-400/20' 
              : 'bg-white/10 border-white/20'
          }`}
        >
           <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              {getStatusIcon()}
              <span className={`text-sm font-medium transition-colors duration-300 ${
                isShowingSuccess ? 'text-green-200' : 'text-white/90'
              }`}>
                Sync Status: {effectiveStatus.message}
              </span>
            </div>
            <span className={`text-sm font-semibold transition-colors duration-300 ${
              isShowingSuccess ? 'text-green-300' : 'text-white/80'
            }`}>{effectiveStatus.progress}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2.5 overflow-hidden backdrop-blur-sm">
            <motion.div
              className={`h-2.5 rounded-full ${getProgressBarColor()} transition-all duration-500`}
              initial={{ width: 0 }}
              animate={{ width: `${effectiveStatus.progress}%` }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            />
          </div>
          {/* Display Details if available */}
          {effectiveStatus.details && effectiveStatus.details.length > 0 && (
            <div className="mt-3 text-xs text-white/70 space-y-1">
              <p className={`font-medium transition-colors duration-300 ${
                isShowingSuccess ? 'text-green-300' : 'text-white/80'
              }`}>Details:</p>
              <ul className="list-disc list-inside pl-2 max-h-20 overflow-y-auto">
                {effectiveStatus.details.map((detail, index) => (
                  <li key={index} className="truncate">{detail}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Success Celebration Message */}
          {isShowingSuccess && completedTaskInfo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-3 text-center"
            >
              <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-green-500/20 border border-green-400/30 rounded-lg">
                <span className="text-lg">🎉</span>
                <span className="text-xs font-medium text-green-200">
                  Sync completed successfully!
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SyncStatusIndicator;